import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import CattLogo from "@/components/CattLogo";

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { t } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { label: t.nav.research, href: "/#domains", key: "research" },
    { label: t.nav.methods, href: "/#methods", key: "methods" },
    { label: t.nav.projects, href: "/#projects", key: "projects" },
    { label: t.nav.publications, href: "/publications", key: "publications", isRoute: true },
    { label: t.nav.lab, href: "/#lab", key: "lab" },
    { label: t.nav.partners, href: "/#partners", key: "partners" },
    { label: t.nav.contact, href: "/#contact", key: "contact" },
  ];

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#101012]/75 border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between gap-6">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group shrink-0">
          <div className="text-white group-hover:text-[#FF3333] transition-colors duration-300">
            <CattLogo size={32} />
          </div>
          <div className="flex items-baseline gap-3">
            <span className="font-display font-bold text-lg tracking-tight leading-none">CATT</span>
            <span className="hidden sm:block w-px h-4 bg-white/25" />
            <span className="hidden sm:block font-ko font-bold text-[15px] tracking-[-0.01em] leading-none text-white/85">
              사용성평가연구센터
            </span>
          </div>
        </Link>

        <nav className="hidden xl:flex items-center gap-7">
          {links.map((l) =>
            l.isRoute ? (
              <Link
                key={l.key}
                to={l.href}
                data-testid={`nav-link-${l.key}`}
                className="font-mono text-[11px] tracking-[0.18em] uppercase text-white/70 hover:text-white transition-colors"
              >
                {l.label}
              </Link>
            ) : (
              <a
                key={l.key}
                href={l.href}
                data-testid={`nav-link-${l.key}`}
                className="font-mono text-[11px] tracking-[0.18em] uppercase text-white/70 hover:text-white transition-colors"
              >
                {l.label}
              </a>
            )
          )}
        </nav>

        <div className="hidden xl:flex items-center gap-4 shrink-0">
          <span className="font-mono text-[10px] tracking-widest text-white/40 uppercase flex items-center gap-2">
            <span className="ticker-tick" /> {t.nav.status}
          </span>
          <a
            href="/#contact"
            data-testid="nav-cta"
            className="font-mono text-[11px] tracking-[0.16em] uppercase px-4 py-2 border border-white/20 hover:bg-[#FF3333] hover:border-[#FF3333] transition-colors"
          >
            {t.nav.cta}
          </a>
        </div>

        <div className="xl:hidden flex items-center gap-3">
          <button
            data-testid="nav-mobile-toggle"
            aria-label="Open menu"
            className="p-2 border border-white/15"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="xl:hidden border-t border-white/10 bg-[#101012]/95 backdrop-blur-xl">
          <div className="px-6 py-6 flex flex-col gap-5">
            {links.map((l) =>
              l.isRoute ? (
                <Link
                  key={l.key}
                  to={l.href}
                  onClick={() => setOpen(false)}
                  className="font-mono text-xs tracking-[0.18em] uppercase text-white/80"
                >
                  {l.label}
                </Link>
              ) : (
                <a
                  key={l.key}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="font-mono text-xs tracking-[0.18em] uppercase text-white/80"
                >
                  {l.label}
                </a>
              )
            )}
            <a
              href="/#contact"
              onClick={() => setOpen(false)}
              className="font-mono text-xs tracking-[0.16em] uppercase px-4 py-3 border border-[#FF3333] bg-[#FF3333] text-center"
            >
              {t.nav.cta}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
